import { HttpClient, HttpBackend } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, defer, map, Observable, ReplaySubject, skip, switchMap, take, timer } from 'rxjs';

abstract class MarketTimeService {

  protected http = new HttpClient(inject(HttpBackend))
  private forceSubject = new BehaviorSubject<void>(undefined)
  private offsetSubject = new ReplaySubject<number>(1)
  readonly offset$ = this.offsetSubject.asObservable()

  private sb = this.forceSubject.pipe(
    switchMap(() => timer(0, 3600 * 1000)),
    switchMap(() => defer(() => {
      const start = Date.now()
      return this.createRequest().pipe(
        map(time => {
          const end = Date.now()
          const drift = (end - start) / 2
          return Math.ceil(time - end + drift)
        })
      )
    }))
  ).subscribe(offset => this.offsetSubject.next(offset))

  protected innerNgOnDestroy() {
    this.sb.unsubscribe()
    this.forceSubject.complete()
    this.offsetSubject.complete()
  }

  protected abstract createRequest(): Observable<number>

  fetchTimeOffset() {
    this.forceSubject.next()
    return this.offset$.pipe(skip(1), take(1))
  }

  setServerTime(time: number) {
    this.offsetSubject.next(time - Date.now())
  }

  setTimeOffset(offset: number) {
    this.offsetSubject.next(offset)
  }
}

@Injectable({
  providedIn: 'root'
})
export class BybitTimeService extends MarketTimeService implements OnDestroy {

  protected createRequest() {
    return this.http.get<{time: number}>('https://api.bybit.com/v5/market/time').pipe(
      map(data => data.time)
    )
  }

  ngOnDestroy(): void {
    this.innerNgOnDestroy()
  }
}

@Injectable({
  providedIn: 'root'
})
export class BinanceUsdmTimeService extends MarketTimeService implements OnDestroy {

  protected createRequest() {
    return this.http.get<{serverTime: number}>('https://fapi.binance.com/fapi/v1/time').pipe(
      map(data => data.serverTime)
    )
  }

  ngOnDestroy(): void {
    this.innerNgOnDestroy()
  }
}

@Injectable({
  providedIn: 'root'
})
export class OkxTimeService extends MarketTimeService implements OnDestroy {

  protected createRequest() {
    return this.http.get<{data: [{ts: string}]}>('https://www.okx.com/api/v5/public/time').pipe(
      map(data => parseInt(data.data[0].ts))
    )
  }

  ngOnDestroy(): void {
    this.innerNgOnDestroy()
  }
}
